import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiGet, apiPost, apiRequest, apiUpload, UnauthorizedError } from '../api';
import UnknownFilesAlert from '../components/UnknownFilesAlert';
import {
  Icon, Modal, Pager, TunPicker, fmtEpoch, fmtNum, fmtSize, useToast,
} from '../components/ui';

const OUTPUT_PATH = 'DATA/ORDERS/Output';

// โฟลเดอร์ที่ใช้บ่อยในงาน BCP — Output ดูแบบ recursive (PDF/, TXT/, ...) ที่เหลือดูเฉพาะชั้นนั้น
const FOLDERS = [
  { label: 'Output', path: OUTPUT_PATH, hint: 'ผลลัพธ์ที่ลูกค้าดึงทาง SFTP' },
  { label: 'รอประมวลผล', path: 'DATA/ORDERS', hint: 'ไฟล์ที่เข้า TUN แล้วแต่ยังไม่ถูกประมวลผล' },
  { label: 'Backup', path: 'DATA/ORDERS/Backup', hint: 'ต้นฉบับที่ประมวลผลแล้ว' },
  { label: 'PDFback', path: 'DATA/ORDERS/Backup/PDFback', hint: 'ต้นฉบับที่ PDF-REPORT ใช้แล้ว' },
  { label: 'Error', path: 'DATA/ORDERS/Error', hint: 'ไฟล์ที่ประมวลผลไม่สำเร็จ' },
];

const DEST_FOLDERS = [
  { label: 'ORDERS', path: 'DATA/ORDERS' },
  { label: 'Backup', path: 'DATA/ORDERS/Backup' },
  { label: 'PDFback', path: 'DATA/ORDERS/Backup/PDFback' },
  { label: 'Error', path: 'DATA/ORDERS/Error' },
  { label: 'Output', path: OUTPUT_PATH },
  { label: 'Output/PDF', path: 'DATA/ORDERS/Output/PDF' },
];

const ROLE_LABEL = { source: 'ต้นฉบับ EDI', pdf: 'PDF', output: 'ไฟล์ interface' };
const PAGE_SIZE = 50;
const DEFAULT_EMAIL_FROM = 'noreply@ttnbcp.cloud';
const keyOf = (it) => `${it.tun}::${it.path}`;

export default function Files() {
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const [allTuns, setAllTuns] = useState([]);
  const [selTuns, setSelTuns] = useState(() => (searchParams.get('tun') ? [searchParams.get('tun')] : []));
  const [folder, setFolder] = useState(() => {
    const f = searchParams.get('folder');
    return FOLDERS.some((x) => x.path === f) ? f : OUTPUT_PATH;
  });
  // v2.0.4: มาจากหน้าภาพรวมด้วย ?tun=X&ic=REF&date=YYYYMMDD → แสดงเฉพาะไฟล์ของ interchange นั้น (ต้นฉบับ + PDF + interface)
  // กดเลือกโฟลเดอร์ หรือเปลี่ยน TUN = ออกจากโหมดนี้กลับไปดูตามโฟลเดอร์ปกติ
  const [ic, setIc] = useState(() => (searchParams.get('tun') && searchParams.get('ic')
    ? { ref: searchParams.get('ic'), date: searchParams.get('date') || '' } : null));
  const [files, setFiles] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadErr, setLoadErr] = useState(null);
  const [ext, setExt] = useState('all');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState(new Map());

  useEffect(() => {
    (async () => {
      try { setAllTuns((await apiGet('/api/files/tuns')).tuns || []); } catch (e) { /* ProtectedRoute handles auth */ }
    })();
  }, []);

  const effectiveTuns = selTuns.length ? selTuns : allTuns;

  // v2.0.2: กันคำตอบเก่ามาทับ — กดเปลี่ยนโฟลเดอร์/TUN เร็ว ๆ แล้ว request เดิม (เช่น PDFback ของ 574 TUN)
  // กลับมาช้ากว่าอันใหม่ ตารางเคยโชว์ไฟล์ PDFback ใต้หัว "Output" (เสี่ยงติ๊กแล้วลบผิดโฟลเดอร์) → ใช้เฉพาะคำตอบล่าสุด
  const loadSeq = useRef(0);
  // v2.2.8: หัวการ์ด Output + แถบเครื่องมือ + หัวตารางไม่เลื่อนตามเวลาเลื่อนดูไฟล์ — วัดความสูงแถบที่ตรึงไว้ให้หัวตารางต่อใต้พอดี
  const stickyRef = useRef(null);
  const [stickyH, setStickyH] = useState(0);
  useEffect(() => {
    const el = stickyRef.current; if (!el) return undefined;
    const ro = new ResizeObserver(() => setStickyH(el.offsetHeight));
    ro.observe(el); setStickyH(el.offsetHeight);
    return () => ro.disconnect();
  }, []);
  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    if (!effectiveTuns.length) { setFiles(null); return; }
    setLoading(true);
    setLoadErr(null);
    const tuns = encodeURIComponent(effectiveTuns.join(','));
    try {
      const out = [];
      if (ic && selTuns.length === 1) {
        const t = selTuns[0];
        const data = await apiGet(`/api/files/interchange?tun=${encodeURIComponent(t)}&ref=${encodeURIComponent(ic.ref)}${ic.date ? `&date=${ic.date}` : ''}`);
        (data.entries || []).forEach((f) => out.push({
          tun: t, path: f.rel_path, name: f.name, size: f.size, mtime: f.mtime, role: f.role,
        }));
      } else if (folder === OUTPUT_PATH) {
        const data = await apiGet(`/api/files/output-files?tuns=${tuns}`);
        Object.entries(data.results || {}).forEach(([tun, entries]) => entries.forEach((f) => out.push({
          tun, path: f.rel_path, name: f.name, size: f.size, mtime: f.mtime,
        })));
      } else {
        const data = await apiGet(`/api/files/list-multi?tuns=${tuns}&path=${encodeURIComponent(folder)}`);
        Object.entries(data.results || {}).forEach(([tun, r]) => (r.entries || []).filter((e) => !e.is_dir).forEach((f) => out.push({
          tun, path: f.rel_path, name: f.name, size: f.size, mtime: f.mtime,
        })));
      }
      if (seq !== loadSeq.current) return; // มี request ใหม่กว่าแล้ว ทิ้งคำตอบนี้
      // โหมด interchange: ต้นฉบับ EDI → ไฟล์ interface → PDF แล้วค่อยเรียงตามเวลา
      const ROLE_ORDER = { source: 0, output: 1, pdf: 2 };
      out.sort((a, b) => ((ROLE_ORDER[a.role] ?? 9) - (ROLE_ORDER[b.role] ?? 9)) || (b.mtime - a.mtime));
      setFiles(out);
    } catch (e) {
      if (seq !== loadSeq.current) return;
      if (!(e instanceof UnauthorizedError)) setLoadErr('โหลดรายการไฟล์ไม่สำเร็จ');
      setFiles([]);
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [effectiveTuns, folder, ic, selTuns]);

  useEffect(() => { load(); }, [load]);
  // เปลี่ยนโฟลเดอร์/TUN = ล้างตารางทันที (ขึ้น "กำลังโหลด…") ไม่โชว์ไฟล์ของโฟลเดอร์เดิมค้างระหว่างรอ
  useEffect(() => { setFiles(null); setSelected(new Map()); setPage(0); setExt('all'); }, [folder, selTuns, ic]);
  const pickFolder = (p) => { setIc(null); setFolder(p); };
  const pickTuns = (v) => { setIc(null); setSelTuns(v); };
  useEffect(() => { setPage(0); }, [ext, q]);

  const extOf = (name) => (name.includes('.') ? name.split('.').pop().toLowerCase() : '(none)');
  const extCounts = useMemo(() => {
    const c = {};
    (files || []).forEach((f) => { const e = extOf(f.name); c[e] = (c[e] || 0) + 1; });
    return Object.entries(c).sort((a, b) => b[1] - a[1]);
  }, [files]);

  const visible = (files || []).filter((f) => (ext === 'all' || extOf(f.name) === ext)
    && (!q || f.path.toLowerCase().includes(q.toLowerCase())));
  const pages = Math.ceil(visible.length / PAGE_SIZE);
  const shown = visible.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const tunsWithFiles = new Set((files || []).map((f) => f.tun)).size;
  const selItems = [...selected.values()];
  const allVisibleSelected = visible.length > 0 && visible.every((f) => selected.has(keyOf(f)));

  const toggle = (f) => setSelected((prev) => {
    const next = new Map(prev);
    if (next.has(keyOf(f))) next.delete(keyOf(f)); else next.set(keyOf(f), { tun: f.tun, path: f.path });
    return next;
  });
  const toggleAllVisible = () => setSelected((prev) => {
    const next = new Map(prev);
    if (allVisibleSelected) visible.forEach((f) => next.delete(keyOf(f)));
    else visible.forEach((f) => next.set(keyOf(f), { tun: f.tun, path: f.path }));
    return next;
  });

  const afterAction = () => { setSelected(new Map()); load(); };

  // ── อัปโหลดเข้า RETAILER-DATA ─────────────────────────────────────────────────────────
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const doUpload = async (fileList) => {
    if (!fileList || !fileList.length) return;
    const form = new FormData();
    form.append('destination', JSON.stringify({ kind: 'retailer-data' }));
    [...fileList].forEach((f) => form.append('files', f));
    setUploading(true);
    try {
      const data = await apiUpload('/api/files/upload', form);
      const errors = (data.results || []).filter((r) => r.status === 'error');
      if (errors.length) toast(`อัปโหลดไม่สำเร็จ ${errors.length} ไฟล์: ${errors.map((e) => e.error).join(', ')}`, 'error');
      else toast(`อัปโหลด ${data.results.length} ไฟล์เข้า RETAILER-DATA แล้ว ระบบจะแยกตาม TUN ภายใน 1 นาที`);
    } catch (e) {
      toast('อัปโหลดไม่สำเร็จ ติดต่อเซิร์ฟเวอร์ไม่ได้', 'error');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // ── ดาวน์โหลด ──────────────────────────────────────────────────────────────────────────
  const [downloading, setDownloading] = useState(false);
  const download = async (items) => {
    if (items.length === 1) {
      window.location = `/api/files/download?tun=${encodeURIComponent(items[0].tun)}&path=${encodeURIComponent(items[0].path)}`;
      return;
    }
    setDownloading(true);
    try {
      const res = await apiRequest('/api/files/download-zip', { method: 'POST', body: JSON.stringify({ items }) });
      if (!res.ok) { const d = await res.json().catch(() => ({})); toast(d.error || 'ดาวน์โหลดไม่สำเร็จ', 'error'); return; }
      const cd = res.headers.get('Content-Disposition') || '';
      const match = cd.match(/filename="?([^"]+?)"?$/);
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = match ? match[1] : 'ttnbcp-files.zip';
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 10000);
    } catch (e) {
      toast('ดาวน์โหลดไม่สำเร็จ', 'error');
    } finally {
      setDownloading(false);
    }
  };

  // ── ย้าย / คัดลอก ──────────────────────────────────────────────────────────────────────
  const [dest, setDest] = useState(null); // { action, items }
  const [destKind, setDestKind] = useState('tun');
  const [destTun, setDestTun] = useState('');
  const [destPath, setDestPath] = useState('DATA/ORDERS/Backup');
  const [destBusy, setDestBusy] = useState(false);
  const [destErr, setDestErr] = useState(null);
  const openDest = (action) => { setDest({ action, items: selItems }); setDestErr(null); setDestBusy(false); };
  const confirmDest = async () => {
    if (destKind === 'tun' && (!destTun.trim() || !destPath.trim())) { setDestErr('ระบุ TUN และโฟลเดอร์ปลายทาง'); return; }
    const destination = destKind === 'retailer-data' ? { kind: 'retailer-data' } : { kind: 'tun', tun: destTun.trim(), path: destPath.trim() };
    setDestBusy(true);
    setDestErr(null);
    try {
      const data = await apiPost(dest.action === 'move' ? '/api/files/bulk-move' : '/api/files/bulk-copy', { items: dest.items, destination });
      const errors = (data.results || []).filter((r) => r.status === 'error');
      if (errors.length) {
        setDestErr(`ไม่สำเร็จ ${errors.length} ไฟล์: ${errors.map((e) => e.error).join(', ')}`);
      } else {
        toast(`${dest.action === 'move' ? 'ย้าย' : 'คัดลอก'} ${data.results.length} ไฟล์แล้ว`);
        setDest(null);
      }
      afterAction();
    } catch (e) {
      setDestErr('ทำรายการไม่สำเร็จ ติดต่อเซิร์ฟเวอร์ไม่ได้');
    } finally {
      setDestBusy(false);
    }
  };

  // ── ลบ ────────────────────────────────────────────────────────────────────────────────
  const [del, setDel] = useState(null);
  const [delText, setDelText] = useState('');
  const [delBusy, setDelBusy] = useState(false);
  const [delErr, setDelErr] = useState(null);
  const openDelete = () => { setDel(selItems); setDelText(''); setDelErr(null); };
  const confirmDelete = async () => {
    if (delText !== 'DELETE') { setDelErr('พิมพ์ DELETE ตัวพิมพ์ใหญ่เพื่อยืนยัน'); return; }
    setDelBusy(true);
    try {
      const data = await apiPost('/api/files/bulk-delete', { items: del });
      const errors = (data.results || []).filter((r) => r.status === 'error');
      if (errors.length) setDelErr(`ลบไม่สำเร็จ ${errors.length} ไฟล์`);
      else { toast(`ลบ ${data.results.length} ไฟล์แล้ว`); setDel(null); }
      afterAction();
    } catch (e) {
      setDelErr('ลบไม่สำเร็จ ติดต่อเซิร์ฟเวอร์ไม่ได้');
    } finally {
      setDelBusy(false);
    }
  };

  // ── ส่งอีเมล (บีบอัดไฟล์ที่เลือกเป็น zip แล้วแนบ) ────────────────────────────────────────
  const [mail, setMail] = useState(null);
  const [mailForm, setMailForm] = useState({ from: '', to: '', bcc: '', subject: '', message: '' });
  const [mailHint, setMailHint] = useState('');
  const [mailConfirm, setMailConfirm] = useState(false);
  const [mailBusy, setMailBusy] = useState(false);
  const [mailErr, setMailErr] = useState(null);
  const setMailField = (k) => (e) => setMailForm((f) => ({ ...f, [k]: e.target.value }));
  const openMail = async () => {
    const items = selItems;
    setMail(items);
    setMailForm({ from: DEFAULT_EMAIL_FROM, to: '', bcc: '', subject: '', message: '' });
    setMailHint('');
    setMailConfirm(false);
    setMailErr(null);
    try {
      const s = await apiGet('/api/settings/me');
      setMailForm((f) => ({ ...f, from: s.email_global_from || DEFAULT_EMAIL_FROM, bcc: s.email_global_bcc || '' }));
    } catch (e) { /* keep default */ }
    const tuns = [...new Set(items.map((i) => i.tun))];
    if (tuns.length === 1) {
      try {
        const data = await apiGet(`/api/customers/email?tun=${encodeURIComponent(tuns[0])}`);
        if (data.email) { setMailForm((f) => ({ ...f, to: data.email })); setMailHint(`ดึงจากข้อมูลลูกค้าของ ${tuns[0]}`); }
        else setMailHint(`ไม่มีอีเมลของ ${tuns[0]} ในข้อมูลลูกค้า กรอกเอง`);
      } catch (e) { /* leave blank */ }
    } else {
      setMailHint(`ไฟล์ที่เลือกมาจาก ${tuns.length} TUN กรอกผู้รับเอง`);
    }
  };
  const sendMail = async () => {
    setMailBusy(true);
    setMailErr(null);
    try {
      const data = await apiPost('/api/files/send-email', { items: mail, ...mailForm });
      if (data.ok) { toast(`ส่งอีเมลถึง ${mailForm.to} แล้ว`); setMail(null); }
      else { setMailErr(data.error || 'ส่งไม่สำเร็จ'); setMailConfirm(false); }
    } catch (e) {
      setMailErr('ส่งไม่สำเร็จ ติดต่อเซิร์ฟเวอร์ไม่ได้');
      setMailConfirm(false);
    } finally {
      setMailBusy(false);
    }
  };

  const folderInfo = FOLDERS.find((f) => f.path === folder);
  const relName = (p) => (ic ? p.replace(/^DATA\/ORDERS\//, '') : (p.startsWith(`${folder}/`) ? p.slice(folder.length + 1) : p));

  return (
    <>
      <div className="page-head">
        <div className="page-title">
          <h1>จัดการไฟล์</h1>
          <span className="meta">{allTuns.length ? `${fmtNum(allTuns.length)} TUN` : ''}</span>
        </div>
      </div>

      <UnknownFilesAlert compact />

      <div className="grid-files">
        <section className="card">
          <div className="card-head"><h3>อัปโหลดไฟล์ EDI</h3></div>
          <div className="card-body">
            <div className={`drop${dragOver ? ' over' : ''}`} role="button" tabIndex={0}
              onClick={() => fileInputRef.current?.click()}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click(); }}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); doUpload(e.dataTransfer.files); }}>
              <input ref={fileInputRef} type="file" multiple hidden onChange={(e) => doUpload(e.target.files)} />
              {uploading ? 'กำลังอัปโหลด…' : <>ลากไฟล์มาวางที่นี่ หรือ <b>เลือกไฟล์</b></>}
              <small>ไฟล์จะเข้า RETAILER-DATA แล้วถูกแยกตาม TUN ภายใน 1 นาที</small>
            </div>
            <div className="flow"><span className="code">RETAILER-DATA</span>→<span>แยกตามผู้รับใน UNB</span>→<span className="code">TUN</span>→<span className="code">Output</span></div>
          </div>
        </section>

        <section className="card">
          <div className="card-head">
            <h3>เลือกไฟล์</h3>
            <div className="row">
              {selTuns.length > 0 && <button className="btn-ghost" onClick={() => pickTuns([])}>ล้าง TUN ที่เลือก</button>}
              <button className="btn btn-outline btn-sm" onClick={load} disabled={loading}><Icon name="refresh" />รีเฟรช</button>
            </div>
          </div>
          <div className="card-body controls">
            <div>
              <label className="field-label" htmlFor="files-tun">TUN</label>
              <TunPicker id="files-tun" options={allTuns} value={selTuns} onChange={pickTuns} />
              <div className="hint">ไม่เลือก = ทุก TUN</div>
            </div>
            <div>
              <span className="field-label">โฟลเดอร์</span>
              <div className="chips">
                {FOLDERS.map((f) => (
                  <button key={f.path} className={`chipbtn${!ic && folder === f.path ? ' on' : ''}`} title={f.hint} onClick={() => pickFolder(f.path)}>
                    {f.label}{!ic && folder === f.path && files && <span className="n">{fmtNum(files.length)}</span>}
                  </button>
                ))}
              </div>
              <div className="hint">{ic ? 'กำลังกรองตาม Interchange — กดโฟลเดอร์เพื่อกลับไปดูตามโฟลเดอร์' : folderInfo?.hint}</div>
              {ic && (
                <div className="chips" style={{ marginTop: 6 }}>
                  <button className="chipbtn on" title="ล้างตัวกรอง Interchange" onClick={() => setIc(null)}>
                    Interchange <span className="mono">{ic.ref}</span><Icon name="x" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </section>
      </div>

      <section className="card fm-list" style={{ '--fm-sticky-h': `${stickyH}px` }}>
        <div className="fm-sticky" ref={stickyRef}>
        <div className="card-head">
          <h3>{ic ? `Interchange ${ic.ref}` : folderInfo?.label}<span className="sub">{files ? `${fmtNum(tunsWithFiles)} TUN · ${fmtNum(files.length)} ไฟล์` : ''}</span></h3>
          {extCounts.length > 1 && (
            <div className="seg" role="group" aria-label="ชนิดไฟล์">
              <button className={ext === 'all' ? 'on' : ''} onClick={() => setExt('all')}>ทั้งหมด<span className="n">{files.length}</span></button>
              {extCounts.map(([e, n]) => (
                <button key={e} className={ext === e ? 'on' : ''} onClick={() => setExt(e)}>{e.toUpperCase()}<span className="n">{n}</span></button>
              ))}
            </div>
          )}
        </div>

        <div className={`selbar${selItems.length ? '' : ' idle'}`}>
          <span>{selItems.length ? `เลือก ${fmtNum(selItems.length)} ไฟล์` : 'ติ๊กเลือกไฟล์เพื่อดาวน์โหลด ส่งอีเมล ย้าย หรือลบ'}</span>
          {selItems.length > 0 && <button className="btn-ghost" onClick={() => setSelected(new Map())}>ยกเลิกที่เลือก</button>}
          <span className="spacer" />
          <div className="search" style={{ maxWidth: 260 }}>
            <Icon name="search" />
            <input className="input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="ค้นหาชื่อไฟล์" aria-label="ค้นหาชื่อไฟล์" />
          </div>
          <button className="btn btn-outline btn-sm" disabled={!selItems.length || downloading} onClick={() => download(selItems)}><Icon name="download" />{downloading ? 'กำลังบีบอัด…' : 'ดาวน์โหลด'}</button>
          <button className="btn btn-outline btn-sm" disabled={!selItems.length} onClick={openMail}><Icon name="mail" />ส่งอีเมล</button>
          <button className="btn btn-outline btn-sm" disabled={!selItems.length} onClick={() => openDest('copy')}><Icon name="copy" />คัดลอกไป…</button>
          <button className="btn btn-outline btn-sm" disabled={!selItems.length} onClick={() => openDest('move')}><Icon name="move" />ย้ายไป…</button>
          <button className="btn btn-danger btn-sm" disabled={!selItems.length} onClick={openDelete}><Icon name="trash" />ลบ</button>
        </div>
        </div>

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th className="check"><input type="checkbox" checked={allVisibleSelected} onChange={toggleAllVisible} disabled={!visible.length}
                  aria-label="เลือกทุกไฟล์ที่แสดงตามตัวกรอง" title="เลือกทุกไฟล์ตามตัวกรอง (ทุกหน้า)" /></th>
                <th>ไฟล์</th><th>TUN</th><th>ชนิด</th><th className="r">ขนาด</th><th>แก้ไขล่าสุด</th>
              </tr>
            </thead>
            <tbody>
              {loading && !files ? (
                <tr><td colSpan={6} className="empty">กำลังโหลด…</td></tr>
              ) : loadErr ? (
                <tr><td colSpan={6} className="empty" style={{ color: 'var(--danger)' }}>{loadErr}</td></tr>
              ) : !shown.length ? (
                <tr><td colSpan={6} className="empty">{files && files.length ? 'ไม่มีไฟล์ที่ตรงกับตัวกรอง' : (ic ? `ไม่พบไฟล์ของ Interchange ${ic.ref} ใน ${selTuns[0] || ''} (อาจถูกลบหรือย้ายไปแล้ว)` : `ไม่มีไฟล์ใน ${folderInfo?.label}`)}</td></tr>
              ) : shown.map((f) => {
                const e = extOf(f.name);
                return (
                  <tr key={keyOf(f)} onClick={(ev) => { if (ev.target.tagName !== 'INPUT') toggle(f); }} style={{ cursor: 'pointer' }}>
                    <td className="check"><input type="checkbox" checked={selected.has(keyOf(f))} onChange={() => toggle(f)} aria-label={`เลือก ${f.name}`} /></td>
                    <td><span className="file ink" title={f.path}>{relName(f.path)}</span>{f.role && <span className="muted" style={{ marginLeft: 8, fontSize: 12 }}>{ROLE_LABEL[f.role] || f.role}</span>}</td>
                    <td><span className="code">{f.tun}</span></td>
                    <td><span className={`ext ${e}`}>{e.toUpperCase()}</span></td>
                    <td className="r num nowrap">{fmtSize(f.size)}</td>
                    <td className="num nowrap">{fmtEpoch(f.mtime)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="foot">
          <span>
            {visible.length ? `แสดง ${page * PAGE_SIZE + 1}–${Math.min((page + 1) * PAGE_SIZE, visible.length)} จาก ${fmtNum(visible.length)}` : 'ไม่มีไฟล์'}
            {loading && files ? ' · กำลังรีเฟรช…' : ''}
          </span>
          <Pager page={page} pages={pages} onPage={setPage} />
        </div>
      </section>

      {/* ── ย้าย / คัดลอก ── */}
      <Modal open={!!dest} title={dest?.action === 'move' ? 'ย้ายไฟล์ไป…' : 'คัดลอกไฟล์ไป…'} onClose={() => setDest(null)} busy={destBusy}
        actions={(
          <>
            <button className="btn btn-outline" onClick={() => setDest(null)} disabled={destBusy}>ยกเลิก</button>
            <button className="btn btn-primary" onClick={confirmDest} disabled={destBusy}>{destBusy ? 'กำลังทำ…' : (dest?.action === 'move' ? 'ย้าย' : 'คัดลอก')}</button>
          </>
        )}>
        <p style={{ marginBottom: 10 }}>{fmtNum(dest?.items.length || 0)} ไฟล์ที่เลือก</p>
        <div className="radio-row">
          <label><input type="radio" name="destkind" checked={destKind === 'tun'} onChange={() => setDestKind('tun')} />ไปที่ TUN</label>
          <label><input type="radio" name="destkind" checked={destKind === 'retailer-data'} onChange={() => setDestKind('retailer-data')} />เข้า RETAILER-DATA (ประมวลผลใหม่ตั้งแต่ต้น)</label>
        </div>
        {destKind === 'tun' && (
          <>
            <div className="field">
              <label className="lbl" htmlFor="dest-tun">TUN ปลายทาง</label>
              <input className="input mono" id="dest-tun" list="dest-tun-list" placeholder="เช่น ZPLT" value={destTun} onChange={(e) => setDestTun(e.target.value)} />
              <datalist id="dest-tun-list">{allTuns.map((t) => <option key={t} value={t} />)}</datalist>
            </div>
            <div className="field">
              <label className="lbl" htmlFor="dest-path">โฟลเดอร์ปลายทาง</label>
              <input className="input mono" id="dest-path" value={destPath} onChange={(e) => setDestPath(e.target.value)} />
              <div className="chips" style={{ marginTop: 8 }}>
                {DEST_FOLDERS.map((f) => (
                  <button key={f.path} type="button" className={`chipbtn${destPath === f.path ? ' on' : ''}`} onClick={() => setDestPath(f.path)}>{f.label}</button>
                ))}
              </div>
            </div>
          </>
        )}
        {destErr && <div className="alert alert-error">{destErr}</div>}
      </Modal>

      {/* ── ลบ ── */}
      <Modal open={!!del} title="ลบไฟล์" onClose={() => setDel(null)} busy={delBusy}
        actions={(
          <>
            <button className="btn btn-outline" onClick={() => setDel(null)} disabled={delBusy}>ยกเลิก</button>
            <button className="btn btn-danger-solid" onClick={confirmDelete} disabled={delBusy}>{delBusy ? 'กำลังลบ…' : 'ลบไฟล์'}</button>
          </>
        )}>
        <p>ลบ <b>{fmtNum(del?.length || 0)} ไฟล์</b> ออกจากเครื่อง ถ้าเป็นไฟล์ใน Output ลูกค้าจะดึงทาง SFTP ไม่ได้อีก กู้คืนได้จาก backup บน OCI เท่านั้น (ภายใน 30 วัน)</p>
        <div className="field" style={{ marginTop: 12 }}>
          <label className="lbl" htmlFor="del-confirm">พิมพ์ DELETE เพื่อยืนยัน</label>
          <input className="input mono" id="del-confirm" autoComplete="off" value={delText} onChange={(e) => { setDelText(e.target.value); setDelErr(null); }} />
        </div>
        {delErr && <div className="alert alert-error">{delErr}</div>}
      </Modal>

      {/* ── ส่งอีเมล ── */}
      <Modal open={!!mail} title="ส่งอีเมล" wide onClose={() => setMail(null)} busy={mailBusy}
        actions={mailConfirm ? (
          <>
            <button className="btn btn-outline" onClick={() => setMailConfirm(false)} disabled={mailBusy}>กลับไปแก้</button>
            <button className="btn btn-primary" onClick={sendMail} disabled={mailBusy}><Icon name="mail" />{mailBusy ? 'กำลังส่ง…' : 'ยืนยันส่ง'}</button>
          </>
        ) : (
          <>
            <button className="btn btn-outline" onClick={() => setMail(null)}>ยกเลิก</button>
            <button className="btn btn-primary" disabled={!mailForm.from || !mailForm.to} onClick={() => setMailConfirm(true)}><Icon name="mail" />ส่ง</button>
          </>
        )}>
        <p style={{ marginBottom: 12 }}>บีบอัด {fmtNum(mail?.length || 0)} ไฟล์เป็น zip ไฟล์เดียวแล้วแนบไปกับอีเมล</p>
        <div className="form-grid">
          <div><label className="lbl" htmlFor="m-from">ผู้ส่ง</label><input className="input" id="m-from" value={mailForm.from} onChange={setMailField('from')} disabled={mailConfirm} /></div>
          <div><label className="lbl" htmlFor="m-to">ผู้รับ</label><input className="input" id="m-to" value={mailForm.to} onChange={setMailField('to')} disabled={mailConfirm} placeholder="customer@example.com" />
            {mailHint && <div className="hint">{mailHint}</div>}</div>
          <div className="full"><label className="lbl" htmlFor="m-bcc">สำเนาลับ (Bcc)</label><input className="input" id="m-bcc" value={mailForm.bcc} onChange={setMailField('bcc')} disabled={mailConfirm} placeholder="ไม่บังคับ คั่นด้วยจุลภาค" /></div>
          <div className="full"><label className="lbl" htmlFor="m-sub">หัวเรื่อง</label><input className="input" id="m-sub" value={mailForm.subject} onChange={setMailField('subject')} disabled={mailConfirm} /></div>
          <div className="full"><label className="lbl" htmlFor="m-msg">ข้อความ</label><textarea className="input" id="m-msg" rows={4} value={mailForm.message} onChange={setMailField('message')} disabled={mailConfirm} /></div>
        </div>
        {mailConfirm && <div className="alert alert-warn" style={{ marginTop: 12 }}>ส่งถึง <b>{mailForm.to}</b>{mailForm.bcc ? ` (Bcc: ${mailForm.bcc})` : ''} พร้อมไฟล์แนบ {fmtNum(mail?.length || 0)} ไฟล์ ส่งแล้วเรียกคืนไม่ได้</div>}
        {mailErr && <div className="alert alert-error" style={{ marginTop: 12 }}>{mailErr}</div>}
      </Modal>
    </>
  );
}
